import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// PDF.js types
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

const ViewerContainer = styled.div`
  position: relative;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
`;

const FlipbookWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 0;

  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #FFA500 0%, #FFD700 100%);
  }
`;

const FlipbookContainer = styled.div`
  width: 1200px;
  height: 600px;
  margin: 0 auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  overflow: hidden;
  background: #000;

  .page {
    background: #000 !important;
  }

  @media (max-width: 1300px) {
    width: 900px;
    height: 450px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
    height: 700px;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 380px;
    height: 550px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--color-primary);
  height: 600px;
  gap: 30px;
`;

const LoadingText = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  text-align: center;
  font-family: var(--font-display);
`;

const ProgressContainer = styled.div`
  width: 80%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 4px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
`;

const ProgressBar = styled.div<{ width: number }>`
  height: 30px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border-radius: 16px;
  width: ${props => props.width}%;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #000;
  font-size: 0.9rem;
  box-shadow: 0 2px 10px rgba(255, 215, 0, 0.5);
`;

const LoadingInfo = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  flex-wrap: wrap;
  padding: 0 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
    width: 100%;
  }
`;

const ControlButton = styled.button`
  padding: 1rem;
  min-width: 70px;
  min-height: 70px;
  border-radius: 20px;
  font-weight: 900;
  transition: all 0.3s ease;
  font-family: var(--font-display);
  font-size: 2rem;
  border: 4px solid #000;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #000;
  box-shadow: 0 6px 0 #000, 0 8px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 0 #000, 0 10px 25px rgba(255, 215, 0, 0.6);
  }

  &:active:not(:disabled) {
    transform: translateY(2px);
    box-shadow: 0 2px 0 #000, 0 3px 15px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    min-width: 60px;
    min-height: 60px;
    font-size: 1.5rem;
    border: 3px solid #000;
    padding: 0.8rem;
  }

  @media (max-width: 480px) {
    min-width: 55px;
    min-height: 55px;
    font-size: 1.3rem;
    padding: 0.7rem;
  }
`;

const PageInfo = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 1.2rem;
  color: var(--color-primary);
  font-family: var(--font-display);
`;

const ErrorContainer = styled.div`
  color: #ff6b6b;
  text-align: center;
  padding: 20px;
  background: rgba(255, 107, 107, 0.1);
  border: 2px solid #ff6b6b;
  border-radius: 10px;
  margin: 20px;
`;

const ComicViewer: React.FC = () => {
  const flipbookRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Preparing document...');
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom] = useState(2);

  useEffect(() => {
    loadPDFLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPDFLibrary = () => {
    // Load PDF.js library
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      loadTurnJS();
    };
    document.body.appendChild(script);
  };

  const loadTurnJS = () => {
    // Load jQuery
    const jqueryScript = document.createElement('script');
    jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
    jqueryScript.onload = () => {
      // Load Turn.js
      const turnScript = document.createElement('script');
      turnScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/turn.js/3/turn.min.js';
      turnScript.onload = () => {
        loadPDF();
      };
      document.body.appendChild(turnScript);
    };
    document.body.appendChild(jqueryScript);
  };

  const loadPDF = async () => {
    try {
      setProgress(0);
      setLoadingMessage('Downloading PDF...');

      const url = '/zeus.pdf';
      const loadingTask = window.pdfjsLib.getDocument(url);

      loadingTask.onProgress = (progressData: any) => {
        if (progressData.total > 0) {
          const percent = (progressData.loaded / progressData.total) * 50;
          setProgress(percent);
          setLoadingMessage(`Downloading PDF... ${Math.round((progressData.loaded / progressData.total) * 100)}%`);
        }
      };

      const pdf = await loadingTask.promise;
      setTotalPages(pdf.numPages);
      setProgress(50);
      setLoadingMessage('PDF downloaded, rendering pages...');

      await renderAllPages(pdf);

      setProgress(100);
      setLoadingMessage('Finalizing...');

      await new Promise(resolve => setTimeout(resolve, 200));

      initializeTurnJS();

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const renderAllPages = async (pdf: any) => {
    const flipbook = flipbookRef.current;
    if (!flipbook) return;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const renderPercent = 50 + ((pageNum - 1) / pdf.numPages) * 50;
      setProgress(renderPercent);
      setLoadingMessage(`Rendering page ${pageNum} of ${pdf.numPages}...`);

      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: zoom });

      const pageDiv = document.createElement('div');
      pageDiv.className = 'page';
      pageDiv.id = `page-${pageNum}`;
      pageDiv.style.cssText = 'background: #000; position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; overflow: hidden;';

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.maxWidth = '100%';
      canvas.style.maxHeight = '100%';
      canvas.style.width = 'auto';
      canvas.style.height = 'auto';
      canvas.style.display = 'block';

      pageDiv.appendChild(canvas);
      flipbook.appendChild(pageDiv);

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    }
  };

  const initializeTurnJS = () => {
    if (!flipbookRef.current) return;

    const $ = (window as any).$;
    const $flipbook = $(flipbookRef.current);

    // Detect if mobile
    const isMobile = window.innerWidth <= 768;
    const width = isMobile ? Math.min(window.innerWidth - 40, 500) : 1200;
    const height = isMobile ? 700 : 600;

    $flipbook.turn({
      width: width,
      height: height,
      autoCenter: true,
      acceleration: true,
      gradients: true,
      elevation: 50,
      display: isMobile ? 'single' : 'double', // Single page on mobile
      duration: 1000
    });

    $flipbook.bind('turned', function(_event: any, page: number) {
      setCurrentPage(page);
    });

    setTimeout(() => $flipbook.turn('resize'), 100);
    setTimeout(() => $flipbook.turn('resize'), 300);
    setTimeout(() => $flipbook.turn('resize'), 500);
  };

  const nextPage = () => {
    const $ = (window as any).$;
    $(flipbookRef.current).turn('next');
  };

  const prevPage = () => {
    const $ = (window as any).$;
    $(flipbookRef.current).turn('previous');
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevPage();
      if (e.key === 'ArrowRight') nextPage();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (error) {
    return (
      <ViewerContainer>
        <ErrorContainer>
          <h2>Error loading PDF</h2>
          <p>{error}</p>
          <p>Make sure the file 'zeus.pdf' is available.</p>
        </ErrorContainer>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer>
      {loading && (
        <LoadingContainer>
          <LoadingText>Loading... ⚡</LoadingText>
          <ProgressContainer>
            <ProgressBar width={progress}>
              <span>{Math.round(progress)}%</span>
            </ProgressBar>
          </ProgressContainer>
          <LoadingInfo>{loadingMessage}</LoadingInfo>
        </LoadingContainer>
      )}

      <FlipbookWrapper style={{ display: loading ? 'none' : 'flex' }}>
        <FlipbookContainer ref={flipbookRef} />
      </FlipbookWrapper>

      {!loading && (
        <>
          <Controls>
            <ButtonRow>
              <ControlButton onClick={prevPage} disabled={currentPage <= 1}>
                ◀️
              </ControlButton>
              <ControlButton onClick={nextPage} disabled={currentPage >= totalPages}>
                ▶️
              </ControlButton>
            </ButtonRow>
          </Controls>
          <PageInfo>Page {currentPage} of {totalPages}</PageInfo>
        </>
      )}
    </ViewerContainer>
  );
};

export default ComicViewer;
